/**
 * What the calendar offers to say to the customer — Aaron, Oct 24, note 1.8:
 * "When calendar items get moved that are connected to a contact / property,
 * it should offer to send them an email as an activity that shows up in their
 * inbox", and note 1.7's *send notification*.
 *
 * Only ever words to start a New text or New email with: the person reads
 * them, changes them and presses **Send** — nothing here sends anything, and
 * what is sent goes through the customer's conversation, so it is in the
 * Inbox. A site visit's new time is folder 48's own confirmation instead (the
 * book mode's *Text Renee a confirmation*, with its *Reply C to confirm*), so
 * there is one confirmation and not two.
 */
import { clock, dayComma, dayLong } from '@rowted/shared'

export interface TellFacts {
  kind: 'appointment' | 'meeting'
  /** The customer's first name. */
  firstName: string
  /** Who they will see: the salesperson, the first one going. */
  who: string | null
  date: string
  /** Minutes from midnight. */
  start: number
  /** *412 Orchard Ridge Dr*, or null. */
  where: string | null
  /** By phone or video: no place. */
  remote: boolean
}

const kindWord = (kind: TellFacts['kind']) => (kind === 'appointment' ? 'site visit' : 'meeting')

/** *Thursday, October 29 at 2:00 PM, at 412 Orchard Ridge Dr* — the when and the where. */
function whenWhere(facts: TellFacts): string {
  const where = facts.remote ? ', by phone or video' : facts.where ? `, at ${facts.where}` : ''
  return `${dayLong(facts.date)} at ${clock(facts.start)}${where}`
}

/**
 * A text and an email to start from. `moved`: it has a new time (note 1.8);
 * otherwise a note about it (the menu's *Text Renee*, *Email Renee*), left
 * open for the rest to be written.
 */
export function tellWords(facts: TellFacts, moved: boolean): { text: string; subject: string; email: string } {
  const kind = kindWord(facts.kind)
  const withWho = facts.who ? ` with ${facts.who}` : ''
  if (moved) {
    const text = `Hi ${facts.firstName} — your ${kind}${withWho} has moved to ${whenWhere(facts)}. `
      + 'Reply here if that time doesn’t work for you.'
    return { text, email: text, subject: `New time for your ${kind}: ${dayComma(facts.date)} at ${clock(facts.start)}` }
  }
  const text = `Hi ${facts.firstName} — about your ${kind}${withWho} on ${whenWhere(facts)}: `
  return { text, email: text, subject: `Your ${kind} on ${dayComma(facts.date)} at ${clock(facts.start)}` }
}

/** *Let Renee know?* — the question after a move (note 1.8). */
export const tellQuestion = (firstName: string): string => `Let ${firstName} know?`

/**
 * A meeting's own sheet saved with a new time (*Reschedule*, the sheet's
 * *When*): the calendar, listening, offers to tell the customer (note 1.8).
 */
export const ACTIVITY_MOVED_EVENT = 'rowted:activity-moved'
export interface ActivityMovedDetail { id: string; kind: 'appointment' | 'meeting'; occurrence?: string | null }

export function announceMoved(detail: ActivityMovedDetail): void {
  window.dispatchEvent(new CustomEvent<ActivityMovedDetail>(ACTIVITY_MOVED_EVENT, { detail }))
}
